<template>
  <div class="parenting-builder" :style="{ '--print-hour-row': printHourRow, '--print-hour-font': printHourFont }">
    <header class="banner">
      <div class="banner-titles">
        <h2>{{ activeView === 'year' ? 'Year Calendar' : 'Parenting Time Schedule' }}</h2>
        <p v-if="activeView === 'year'">Your weekly rotation and holiday decisions projected onto real dates.</p>
        <p v-else>{{ rotationDescription }}</p>
      </div>
      <div class="banner-actions">
        <template v-if="activeView === 'rotation' && isPrimary">
          <RotationTemplateMenu @apply="applyTemplate" />
          <button type="button" class="btn-primary" @click="addTimeblock">+ Add Timeblock</button>
          <button type="button" class="btn-secondary" @click="resetAll" title="Clear the schedule">↺ Reset</button>
        </template>
        <button type="button" class="btn-secondary" @click="exportIcs" title="Export to calendar (.ics file for Google / Apple / Outlook)">📅 Export .ics</button>
        <button type="button" class="btn-download" @click="print" title="Print or save this page as a PDF">↓ Download PDF</button>
      </div>
    </header>

    <!-- Shared deliverable header, print only: replaces the teal banner on
         the printed page. The Year Calendar view prints its own copy from
         inside CalendarView, so this one is rotation-view only. -->
    <PrintHeader
      v-if="activeView === 'rotation'"
      print-only
      title="Parenting Time Schedule"
      :partyAName="partyAName"
      :partyBName="partyBName"
      :subtitle="rotationDescription"
    />

    <div v-if="!viewMode" class="view-tabs">
      <button type="button" :class="{ active: view === 'rotation' }" @click="view = 'rotation'">Weekly Rotation</button>
      <button type="button" :class="{ active: view === 'year' }" @click="view = 'year'">Year Calendar</button>
    </div>

    <CalendarView
      v-if="activeView === 'year' && rotation"
      :rotation="rotation"
      :holidays="holidays"
      :jurisdiction="jurisdiction"
      :partyAName="partyAName"
      :partyBName="partyBName"
    />

    <div v-if="activeView === 'rotation' && isPrimary" class="length-bar">
      <label class="length-label">
        <span>Rotation length</span>
        <select :value="rotation.rotationLength" @change="setRotationLength(Number($event.target.value))">
          <option v-for="n in ROTATION_OPTIONS" :key="n" :value="n">{{ n }} week{{ n === 1 ? '' : 's' }}</option>
        </select>
      </label>
      <label class="length-label" title="The real-world Monday that is Day 1 of Week 1. The calendar views and exports project the rotation from this date. Any date you pick snaps to the Monday of that week.">
        <span>Week 1 begins</span>
        <DateField
          class="anchor-input"
          :value="rotation.anchorDate"
          @input="setAnchorDate($event)"
        />
      </label>
      <button type="button" class="btn-link" @click="addWeek" :disabled="rotation.rotationLength >= MAX_WEEKS">+ Add Week</button>
      <button type="button" class="btn-link" @click="removeWeek" :disabled="rotation.rotationLength <= 1">− Remove Last Week</button>
      <button
        type="button"
        class="btn-link btn-continue"
        title="Repeat the timeblocks you have built so far until they fill the whole rotation"
        @click="continuePatternClick"
      >⟳ Continue Pattern</button>
    </div>

    <p v-if="activeView === 'rotation' && !isPrimary" class="viewing-note">
      You are viewing the schedule. Only the party who set it up can change it — open any
      block to see its details.
    </p>
    <p v-if="activeView === 'rotation' && agedOutNote" class="aged-out-note">{{ agedOutNote }}</p>

    <div v-if="activeView === 'rotation'" class="weeks-stack">
      <WeekGrid
        v-for="(week, i) in rotation.weeks"
        :key="i"
        :weekIndex="i"
        :allWeeks="rotation.weeks"
        :partyAName="partyAName"
        :partyBName="partyBName"
        :children="children"
        :jurisdiction="jurisdiction"
        :role="role"
        :totalWeeks="rotation.weeks.length"
        :distribution="distribution.perWeek[i]"
        @edit-event="openEditModal"
        @resize-event="resizeEvent"
        @add-event="addTimeblockToWeek"
        @add-event-at="addTimeblockAt"
      />
    </div>

    <div v-if="activeView === 'rotation' && hasEvents" class="overall-dist">
      <h4>Overall time distribution — {{ rotationDescription }}</h4>
      <div class="dist-bar">
        <span class="dist-a" :style="{ width: overallPct('A') + '%' }"></span>
        <span class="dist-b" :style="{ width: overallPct('B') + '%' }"></span>
        <span v-if="distribution.total.unassigned" class="dist-gap" :style="{ width: overallPct('unassigned') + '%' }"></span>
      </div>
      <p class="overall-dist-legend">
        <span><strong>{{ partyAName || 'Party A' }}</strong>: {{ fmtDur(distribution.total.A) }} ({{ fmtPct(distribution.total.A) }}%)</span>
        <span><strong>{{ partyBName || 'Party B' }}</strong>: {{ fmtDur(distribution.total.B) }} ({{ fmtPct(distribution.total.B) }}%)</span>
        <span v-if="distribution.total.unassigned" class="dist-unassigned-note">
          Unassigned: {{ fmtDur(distribution.total.unassigned) }} ({{ fmtPct(distribution.total.unassigned) }}%)
        </span>
      </p>
    </div>

    <EditEventModal
      :event="editModalState"
      :partyAName="partyAName"
      :partyBName="partyBName"
      :role="role"
      :currentUserName="currentUserName"
      :totalWeeks="rotation ? rotation.weeks.length : 2"
      @save="saveEvent"
      @delete="deleteEvent"
      @edit-host="openEditModal"
      @add-visit="addVisitFromEdit"
      @cancel="closeEditModal"
    />
  </div>
</template>

<script>
import WeekGrid from './components/WeekGrid.vue'
import DateField from '../DateField.vue'
import EditEventModal from './components/EditEventModal.vue'
import RotationTemplateMenu from './components/RotationTemplateMenu.vue'
import CalendarView from '../CalendarView.vue'
import PrintHeader from '../PrintHeader.vue'
import { load, save, clear, flushSave } from './rotationDraft.js'
import dialogs from '../dialogs.js'
import { blankWeek, blankRotation } from '@/lib/parentingSchedule/rotationTemplates.js'
import { continuePattern, formatPatternLength, articleFor } from '@/lib/parentingSchedule/patternRepeat'
import { computeTimeDistribution, formatDurationShort, formatPercent } from '@/lib/parentingSchedule/timeDistribution'
import { splitTimeblock, flattenEvent, positionFromInterval } from '@/lib/parentingSchedule/splitTimeblock'
import { generateRecurringIcs } from '@/lib/parentingSchedule/icsExport'
import { partyFileTitle } from '@/lib/agreement/filters'
import { childrenSubject, agedOutNote, lastNameOf } from '@/lib/parentingSchedule/children'
import { MIN_PER_DAY } from '@/lib/parentingSchedule/weekMath'

// 12-week cap: shift patterns that don't align to 7-day weeks need room to
// loop cleanly — a 12-day pattern (6-on/6-off) repeats evenly only in a
// 12-week rotation (84 days = 7 repeats).
const MAX_WEEKS = 12
const ROTATION_OPTIONS = Array.from({ length: MAX_WEEKS }, (_, i) => i + 1)

export default {
  name: 'ParentingBuilder',
  components: { WeekGrid, EditEventModal, RotationTemplateMenu, CalendarView, PrintHeader, DateField },
  mixins: [dialogs], // $confirmDialog / $alertDialog
  props: {
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    caseId: { type: String, default: '' },
    children: { type: Array, default: () => [] },
    // Holiday Schedule decisions for the same case (the holiday app's
    // holidays[] array). Holiday rule outcomes override the rotation in the
    // Year Calendar view. In Divii this comes from the sibling
    // cases/{caseId}/holidaySchedule document.
    holidays: { type: Array, default: () => [] },
    jurisdiction: { type: String, default: 'BC' },
    // When a host (e.g. ParentingHub) owns the navigation, it sets viewMode
    // to 'rotation' or 'year' and this component hides its own view tabs.
    // Null (default) keeps the self-managed tabs.
    viewMode: { type: String, default: null },
    role: {
      type: String,
      default: 'primary',
      validator: (v) => v === 'primary' || v === 'secondary' || v === 'viewer',
    },
    currentUserName: { type: String, default: '' },
  },
  data() {
    return {
      ROTATION_OPTIONS,
      MAX_WEEKS,
      // The load is async, so seed a blank rotation to keep the template's
      // `rotation.weeks` accesses safe during the first paint.
      rotation: blankRotation(),
      editModalState: null,
      view: 'rotation', // 'rotation' | 'year'
    }
  },
  computed: {
    isPrimary() { return this.role === 'primary' },
    activeView() { return this.viewMode || this.view },
    // "Monday, March 2, 2026" — shown in the banner (and therefore on the
    // printed agreement attachment: the commencement date is part of the deal).
    anchorLabel() {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(this.rotation?.anchorDate || ''))
      if (!m) return ''
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      return d.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    },
    distribution() {
      return this.rotation
        ? computeTimeDistribution(this.rotation)
        : { perWeek: [], total: { A: 0, B: 0, unassigned: 0 }, cycleMins: 0 }
    },
    // Print fit: a 2-week rotation must land on one page, and the number of
    // transition summaries under the grids is what varies (2/2/3 prints six;
    // a date-night split adds more). Trade hour-row height for that space so
    // a sparse schedule stays readable and a dense one still fits.
    printHourRow() {
      const weeks = this.rotation?.weeks || []
      if (!weeks.length) return '8.5px'
      const total = weeks.reduce((sum, w) => sum + ((w.events && w.events.length) || 0), 0)
      // Two weeks print per page, so what matters is the density of one sheet.
      const perPage = (total / weeks.length) * Math.min(2, weeks.length)
      if (perPage <= 4) return '8.5px'
      if (perPage <= 6) return '7.5px'
      if (perPage <= 8) return '6px'
      return '5px'
    },
    // Hour labels track the row height so dense sheets stay readable.
    printHourFont() {
      return {
        '8.5px': '0.5rem', '7.5px': '0.46rem', '6px': '0.4rem', '5px': '0.34rem',
      }[this.printHourRow] || '0.5rem'
    },
    hasEvents() {
      return !!this.rotation?.weeks?.some((w) => w.events && w.events.length)
    },
    rotationDescription() {
      const n = this.rotation?.rotationLength || 0
      const base = n === 1 ? 'One-Week Rotation' : n === 2 ? 'Two-Week Rotation' : `${n}-Week Rotation`
      return this.anchorLabel ? `${base} · Week 1 begins ${this.anchorLabel}` : base
    },
    // On-screen transparency when a child ages out of the wording (never
    // printed): explains why the transition details name fewer children.
    agedOutNote() {
      return agedOutNote(this.children, this.jurisdiction)
    },
  },
  watch: {
    rotation: {
      deep: true,
      handler(v) { if (v) save(this.caseId, v) },
    },
  },
  // The Firestore-backed store debounces and suppresses its own write echoes,
  // so the load is a plain fire-and-forget await (and beforeDestroy flushes).
  created() {
    this.initRotation()
  },
  beforeDestroy() {
    flushSave()
  },
  methods: {
    setRotationLength(n) {
      if (!this.isPrimary) return
      const bounded = Math.max(1, Math.min(MAX_WEEKS, n))
      const cur = this.rotation.weeks.length
      if (bounded === cur) return
      let weeks = this.rotation.weeks.slice()
      if (bounded > cur) {
        while (weeks.length < bounded) weeks.push(blankWeek())
      } else {
        weeks = weeks.slice(0, bounded)
      }
      this.rotation = { ...this.rotation, rotationLength: bounded, weeks }
    },
    // Set the real-world start of Week 1. Weeks are Monday-anchored, so any
    // picked date snaps to the Monday of its week (the week containing the
    // picked date becomes Week 1). Shifting by one week flips week parity —
    // that's the control lawyers need for "who has the kids this week".
    setAnchorDate(iso) {
      if (!this.isPrimary) return
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
      if (!m) return
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7)) // back to Monday
      const snapped = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      this.rotation = { ...this.rotation, anchorDate: snapped }
    },
    addWeek() {
      if (!this.isPrimary) return
      if (this.rotation.rotationLength >= MAX_WEEKS) return
      this.setRotationLength(this.rotation.rotationLength + 1)
    },
    removeWeek() {
      if (!this.isPrimary) return
      if (this.rotation.rotationLength <= 1) return
      this.setRotationLength(this.rotation.rotationLength - 1)
    },
    async applyTemplate(payload) {
      if (!this.isPrimary) return
      // payload = { key, title, config: { rotationLength, weeks } }
      const proceed = await this.$confirmDialog(
        `Apply the "${payload.title}" template? This replaces any existing timeblocks.`,
        { title: 'Apply template', okTitle: 'Apply template' }
      )
      if (!proceed) return
      this.rotation = {
        ...this.rotation,
        rotationLength: payload.config.rotationLength,
        weeks: payload.config.weeks,
      }
    },
    addTimeblock() {
      if (!this.isPrimary) return
      // Top-bar "+ Add Timeblock" — defaults to Week 1.
      this.addTimeblockToWeek({ weekIndex: 0 })
    },
    // "Continue Pattern": repeat the timeblocks built so far until they fill
    // the whole rotation, wrapping the final copy back to the first block.
    // If the pattern doesn't divide the current rotation evenly (an 8-day
    // 4-on/4-off pattern needs 8 weeks), offer to resize the rotation first.
    async continuePatternClick() {
      if (!this.isPrimary) return
      const names = { partyAName: this.partyAName, partyBName: this.partyBName }
      let res = continuePattern(this.rotation, { maxWeeks: MAX_WEEKS, ...names })
      // A pattern with empty time inside it repeats that empty time too. Most
      // often that means the rotation was lengthened before pressing this
      // button, so the seam-built chain no longer wraps. Say so before asking.
      const idleNote = (r) => {
        const idle = (r.patternMins || 0) - (r.coveredMins || 0)
        if (idle <= 0) return ''
        return `\n\nNote: this pattern includes a ${formatPatternLength(idle)} stretch with no timeblocks, which will repeat too. If you meant a continuous handover pattern, set the rotation length back to what it was when you built the blocks and press Continue Pattern again.`
      }

      if (!res.ok && res.suggestedWeeks) {
        const grow = await this.$confirmDialog(
          `${res.reason}\n\nSwitch to ${articleFor(res.suggestedWeeks)} ${res.suggestedWeeks}-week rotation and continue the pattern?${idleNote(res)}`,
          { title: 'Continue pattern', okTitle: 'Resize and continue' }
        )
        if (!grow) return
        // Rebuild at the new length from the measured chain rather than
        // padding weeks and re-measuring: a chain built across the seam of
        // the current cycle is only contiguous in THAT cycle.
        res = continuePattern(this.rotation, { maxWeeks: MAX_WEEKS, targetWeeks: res.suggestedWeeks, ...names })
        if (!res.ok) { this.$alertDialog(res.reason, { title: 'Continue pattern' }); return }
        this.rotation = { ...this.rotation, rotationLength: res.suggestedWeeks || res.weeks.length, weeks: res.weeks }
        return
      }

      if (!res.ok) { this.$alertDialog(res.reason, { title: 'Continue pattern' }); return }

      const proceed = await this.$confirmDialog(
        `Repeat your ${formatPatternLength(res.patternMins)} pattern to fill the ${this.rotation.weeks.length}-week rotation? This adds ${res.addedCount} timeblocks.${idleNote(res)}`,
        { title: 'Continue pattern', okTitle: 'Repeat pattern' }
      )
      if (!proceed) return
      this.rotation = { ...this.rotation, weeks: res.weeks }
    },
    addTimeblockToWeek({ weekIndex }) {
      if (!this.isPrimary) return
      // Per-week "+ Add Timeblock" — targets the week whose button was clicked.
      this.editModalState = {
        weekIndex,
        eventIndex: -1,
        isNew: true,
        event: {
          partner: 'A',
          startDay: 'mon',
          endDay: 'wed',
          startTime: '00:00',
          endTime: '00:00',
          startWeekOffset: 0,
          endWeekOffset: 0,
          transition: null,
          transitionDetails: '',
        },
      }
    },
    // Double-click on an empty grid area: open the Add Timeblock modal
    // pre-filled with the clicked day/time (2-hour default block). The
    // week offsets can be -1 (the row's leading Sunday belongs to the
    // previous data week); the modal re-homes on save.
    addTimeblockAt({ weekIndex, startWeekOffset, startDay, startTime, endWeekOffset, endDay, endTime }) {
      if (!this.isPrimary) return
      this.editModalState = {
        weekIndex,
        eventIndex: -1,
        isNew: true,
        event: {
          partner: 'A',
          startDay,
          startTime,
          endDay,
          endTime,
          startWeekOffset,
          endWeekOffset,
          transition: null,
          transitionDetails: '',
        },
      }
    },
    // "+ Add a Visit (Date Night)" from the edit modal: carve a short visit
    // out of the block being edited, for the other parent. Prefills the
    // first 5-7pm slot that fits inside the block (or the block's first
    // hours as a fallback); on save the block splits into before / visit /
    // after pieces.
    addVisitFromEdit({ weekIndex, eventIndex }) {
      if (!this.isPrimary) return
      const hostEvent = this.rotation.weeks[weekIndex]?.events[eventIndex]
      if (!hostEvent) return
      const { start: S, end: E } = flattenEvent(hostEvent, weekIndex)
      let s = null
      for (let d = Math.floor(S / MIN_PER_DAY); d * MIN_PER_DAY + 17 * 60 + 120 <= E; d++) {
        const cand = d * MIN_PER_DAY + 17 * 60
        if (cand >= S) { s = cand; break }
      }
      let e
      if (s === null) { s = S + 60; e = Math.min(S + 180, E) } else { e = s + 120 }
      const at = positionFromInterval(s, e, this.rotation.weeks.length)
      this.openVisitModal({ weekIndex, eventIndex }, at)
    },
    openVisitModal(host, at) {
      const hostEvent = this.rotation.weeks[host.weekIndex]?.events[host.eventIndex]
      if (!hostEvent) return
      const visitPartner = hostEvent.partner === 'A' ? 'B' : 'A'
      const hostName = hostEvent.partner === 'A' ? (this.partyAName || 'Party A') : (this.partyBName || 'Party B')
      this.editModalState = {
        weekIndex: at.weekIndex,
        eventIndex: -1,
        isNew: true,
        split: host,
        splitLabel: `This visit is carved out of ${hostName}'s timeblock, which will split around it.`,
        event: {
          partner: visitPartner,
          startDay: at.startDay,
          startTime: at.startTime,
          endDay: at.endDay,
          endTime: at.endTime,
          startWeekOffset: at.startWeekOffset,
          endWeekOffset: at.endWeekOffset,
          transition: `${visitPartner}_drops`,
          transitionDetails: '',
        },
      }
    },
    // Apply a saved visit: split the host block around it.
    saveSplit({ targetWeekIndex, event, split }) {
      if (!this.isPrimary) return
      const res = splitTimeblock(this.rotation.weeks, split, { weekIndex: targetWeekIndex, event })
      if (!res.ok) { this.$alertDialog(res.reason, { title: 'Cannot split timeblock' }); return }
      this.rotation = { ...this.rotation, weeks: res.weeks }
      this.closeEditModal()
    },
    openEditModal({ weekIndex, eventIndex }) {
      const event = this.rotation.weeks[weekIndex]?.events[eventIndex]
      if (!event) return
      this.editModalState = {
        weekIndex,
        eventIndex,
        isNew: false,
        event: { ...event },
      }
    },
    closeEditModal() { this.editModalState = null },
    // targetWeekIndex may differ from weekIndex: the modal re-homes an event
    // into the data week where it starts (e.g. a block starting on the Sunday
    // displayed at the head of Week 2's row belongs in Week 1's events[]).
    saveEvent({ weekIndex, targetWeekIndex, eventIndex, isNew, event, split }) {
      if (!this.isPrimary) return
      if (split) return this.saveSplit({ targetWeekIndex: targetWeekIndex ?? weekIndex, event, split })
      const target = targetWeekIndex === undefined ? weekIndex : targetWeekIndex
      const weeks = this.rotation.weeks.map((w, i) => {
        const removeHere = !isNew && i === weekIndex
        const addHere = i === target
        if (!removeHere && !addHere) return w
        const events = w.events.slice()
        if (removeHere && addHere) events.splice(eventIndex, 1, event)
        else if (removeHere) events.splice(eventIndex, 1)
        else events.push(event)
        return { ...w, events }
      })
      this.rotation = { ...this.rotation, weeks }
      this.closeEditModal()
    },
    // Called by WeekGrid while the user drags a top/bottom handle on an event.
    // Fires on every pointermove that crosses a 15-min snap boundary — Vue's
    // reactivity re-renders the block at its new size in real time.
    resizeEvent({ weekIndex, eventIndex, edge, newTime }) {
      if (!this.isPrimary) return
      const weeks = this.rotation.weeks.map((w, i) => {
        if (i !== weekIndex) return w
        const events = w.events.slice()
        const cur = events[eventIndex]
        if (!cur) return w
        const patched = { ...cur }
        if (edge === 'start') patched.startTime = newTime
        else patched.endTime = newTime
        events.splice(eventIndex, 1, patched)
        return { ...w, events }
      })
      this.rotation = { ...this.rotation, weeks }
    },
    deleteEvent({ weekIndex, eventIndex }) {
      if (!this.isPrimary) return
      const weeks = this.rotation.weeks.map((w, i) => {
        if (i !== weekIndex) return w
        const events = w.events.slice()
        events.splice(eventIndex, 1)
        return { ...w, events }
      })
      this.rotation = { ...this.rotation, weeks }
      this.closeEditModal()
    },
    fmtDur: formatDurationShort,
    fmtPct(mins) { return formatPercent(mins, this.distribution.cycleMins) },
    overallPct(key) {
      return this.distribution.cycleMins
        ? (this.distribution.total[key] / this.distribution.cycleMins) * 100
        : 0
    },
    async initRotation() {
      this.rotation = await load(this.caseId)
    },
    async resetAll() {
      if (!this.isPrimary) return
      const proceed = await this.$confirmDialog(
        'Clear the entire schedule and start over? Any timeblocks you have built will be removed.',
        { title: 'Reset schedule', okTitle: 'Clear schedule' }
      )
      if (!proceed) return
      this.rotation = await clear(this.caseId)
    },
    // The browser's save-as-PDF dialog suggests document.title as the
    // filename, so set it to "[LastName][LastName] <Document Name>" for the
    // duration of the print call (window.print blocks until the dialog
    // closes), then restore the app title.
    print() {
      const docName = this.activeView === 'year' ? 'Year Calendar' : 'Parenting Time Schedule'
      const prev = document.title
      document.title = partyFileTitle(lastNameOf(this.partyAName), lastNameOf(this.partyBName), docName)
      // The host page carries the app-chrome print rules (nav,
      // rail, Intercom, Bootstrap gutters) gated on this body class — see
      // ParentingSchedulePanel.vue. Cleanup runs on afterprint rather than in
      // a finally block because window.print() returns early in some browsers.
      document.body.classList.add('parenting-schedule-printing')
      const cleanup = () => {
        document.title = prev
        document.body.classList.remove('parenting-schedule-printing')
        window.removeEventListener('afterprint', cleanup)
      }
      window.addEventListener('afterprint', cleanup)
      window.print()
    },
    // Download the schedule as TWO .ics files — one per parent, each a
    // color-tagged calendar (teal for Party A, Divii yellow for Party B) so
    // the parties get a color-coded view once both are imported. Each file
    // holds that parent's rotation as RECURRING series (3-year horizon) plus
    // their holiday-decision events; shared holidays appear in both files.
    // Recurring series keep mid-year changes easy: delete a few series,
    // import the new files — no week-by-week cleanup. A live-updating
    // subscription URL is the Divii-backend variant — see INTEGRATION.md
    // Phase 4; it reuses the same schedule-core generator.
    exportIcs() {
      const childrenLabel = childrenSubject(this.children, this.jurisdiction, { fallback: '' })
      const from = new Date()
      const until = new Date(from.getFullYear() + 3, from.getMonth(), from.getDate())
      const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'party'
      const shared = {
        rotation: this.rotation,
        holidays: this.holidays,
        jurisdiction: this.jurisdiction,
        partyAName: this.partyAName,
        partyBName: this.partyBName,
        from,
        until,
        now: new Date(),
      }
      const files = [
        {
          filename: `parenting-time-${slug(this.partyAName || 'party-a')}.ics`,
          ics: generateRecurringIcs({
            ...shared,
            forParty: 'A',
            calendarName: `${this.partyAName || 'Party A'}'s parenting time${childrenLabel ? ` with ${childrenLabel}` : ''}`,
            color: { css: 'teal', hex: '#34555F' },
          }),
        },
        {
          filename: `parenting-time-${slug(this.partyBName || 'party-b')}.ics`,
          ics: generateRecurringIcs({
            ...shared,
            forParty: 'B',
            calendarName: `${this.partyBName || 'Party B'}'s parenting time${childrenLabel ? ` with ${childrenLabel}` : ''}`,
            color: { css: 'gold', hex: '#F7A91B' },
          }),
        },
      ]
      for (const f of files) {
        const blob = new Blob([f.ics], { type: 'text/calendar;charset=utf-8' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = f.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(a.href)
      }
    },
  },
}
</script>

<style scoped>
.parenting-builder {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.banner {
  background: #34555f;
  color: #fff;
  border-radius: 10px;
  padding: 0.95rem 1.15rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.banner-titles h2 {
  margin: 0 0 0.1rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.banner-titles p {
  margin: 0;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.78);
}
.banner-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}
.btn-primary,
.btn-secondary {
  font-family: inherit;
  font-size: 0.82rem;
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
}
.btn-primary {
  background: #f7a91b;
  color: #1e293b;
  border-color: #f7a91b;
}
.btn-primary:hover { background: #d8911a; border-color: #d8911a; }
.btn-secondary {
  background: transparent;
  color: #fff;
  border-color: rgba(255,255,255,0.4);
}
.btn-secondary:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.6); }
/* The one button every tab shares: gold Download PDF, rightmost in the
   banner here and in the Holiday Schedule's banner. Keep this rule in sync
   with .btn-download in HolidayBuilder.vue. */
.btn-download {
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  background: #f7a91b;
  border: 1px solid #f7a91b;
  color: #1e293b;
}
.btn-download:hover { background: #d8911a; border-color: #d8911a; }

.view-tabs {
  display: flex;
  gap: 0.4rem;
}
.view-tabs button {
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.4rem 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
}
.view-tabs button.active {
  background: #34555f;
  border-color: #34555f;
  color: #fff;
}
.view-tabs button:not(.active):hover { border-color: #3f6772; color: #34555f; }

.length-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.55rem 0.9rem;
  font-size: 0.82rem;
  flex-wrap: wrap;
}
.length-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #34555f;
  font-weight: 500;
}
.length-label select {
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  font-size: 0.82rem;
  font-family: inherit;
  color: #1e293b;
}
.btn-link {
  background: transparent;
  border: 1px dashed #cbd5e1;
  color: #557d89;
  font-family: inherit;
  font-size: 0.78rem;
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}
.btn-link:hover:not(:disabled) {
  border-color: #3f6772;
  border-style: solid;
  background: #eef3f4;
  color: #34555f;
}
.btn-link:disabled { opacity: 0.4; cursor: not-allowed; }

.weeks-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.viewing-note {
  margin: 0;
  padding: 0.5rem 0.9rem;
  background: #eef3f4;
  border-left: 3px solid #7797a1;
  border-radius: 0 6px 6px 0;
  font-size: 0.8rem;
  color: #3f6772;
}

.aged-out-note {
  margin: 0;
  padding: 0.5rem 0.9rem;
  background: #fef6e0;
  border-left: 3px solid #f7a91b;
  border-radius: 0 6px 6px 0;
  font-size: 0.8rem;
  color: #85581a;
}

.overall-dist {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.9rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.overall-dist h4 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #34555f;
}
.dist-bar {
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background: #f1f5f9;
}
.dist-a {
  background: #7797a1;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.dist-b {
  background: #34555f;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.dist-gap {
  background: repeating-linear-gradient(45deg, #fcd34d, #fcd34d 4px, #fef3c7 4px, #fef3c7 8px);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.overall-dist-legend {
  margin: 0;
  font-size: 0.85rem;
  color: #1e293b;
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
}
.overall-dist-legend strong { color: #34555f; }
.dist-unassigned-note { color: #b45309; }

@media (min-width: 1100px) {
  .weeks-stack {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  /* Odd-week-count last week takes full width */
  .weeks-stack > :last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
}

@media print {
  /* The teal app banner stays on screen only; the shared PrintHeader
     (same block on every printed deliverable) takes its place on paper. */
  .banner,
  .view-tabs,
  .length-bar,
  .aged-out-note,
  .viewing-note,
  .btn-link { display: none !important; }

  /* Bottom clearance for the fixed Divii footer mark (66px wide ≈ 22px
     tall): the last block ends above it instead of shrinking in width. */
  .parenting-builder { gap: 0.4rem; padding-bottom: 26px; }

  /* One column of weeks, two per portrait page: never split a week across
     pages, and break after every second week. No forced break after the
     final week (avoids a trailing blank page on even week counts). */
  .weeks-stack { display: block !important; }
  .weeks-stack > * {
    margin-bottom: 0.3rem;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .weeks-stack > :nth-child(even) {
    break-after: page;
    page-break-after: always;
  }
  .weeks-stack > :last-child {
    break-after: auto;
    page-break-after: auto;
  }

  /* Full width like every other block; type sized to match the printed
     week summaries (0.68rem) and per-week distribution legends (0.66rem).
     The logo clearance comes from the builder's padding-bottom above. */
  .overall-dist {
    padding: 0.3rem 0.55rem;
    gap: 0.2rem;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .overall-dist h4 { font-size: 0.68rem; }
  .overall-dist-legend { font-size: 0.66rem; }
  .dist-bar { height: 6px; }
}
</style>
